import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import AdminTabs from '../../components/AdminTabs';
import { supabase } from '../../lib/supabase';
import type { Tip, TipStatus } from '@viet-parenting/supabase/types';

type Row = Pick<
  Tip,
  'id' | 'tip' | 'submitter_name' | 'submitter_email' | 'country' | 'status' | 'replied_at' | 'created_at'
>;

interface State {
  loading: boolean;
  rows: Row[];
  error: string | null;
}

const STATUS_FILTERS: ReadonlyArray<{ value: TipStatus | 'all'; label: string }> = [
  { value: 'all', label: 'Tất cả' },
  { value: 'pending', label: 'Chờ xử lý' },
  { value: 'done', label: 'Đã xong' },
];

const STATUS_LABEL: Record<TipStatus, string> = {
  pending: 'Chờ xử lý',
  done: 'Đã xong',
};

const STATUS_PILL: Record<TipStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-100',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-100',
};

function truncate(text: string, max = 90): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export default function AdminTips() {
  const [state, setState] = useState<State>({ loading: true, rows: [], error: null });
  const [filter, setFilter] = useState<TipStatus | 'all'>('all');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      const { data, error } = await supabase
        .from('tips')
        .select('id, tip, submitter_name, submitter_email, country, status, replied_at, created_at')
        .order('created_at', { ascending: false });
      if (!active) return;
      setState({
        loading: false,
        rows: (data ?? []) as unknown as Row[],
        error: error?.message ?? null,
      });
    })();
    return () => {
      active = false;
    };
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return state.rows.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false;
      if (!q) return true;
      const haystack = [r.tip, r.submitter_name ?? '', r.submitter_email ?? '', r.country ?? '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [state.rows, filter, query]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <AdminTabs active="tips" />

        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-red-700 mb-1">Gợi ý từ người dùng</h1>
            <p className="text-sm text-gray-500">Gợi ý do khách gửi qua trang web.</p>
          </div>
          <p className="text-xs text-gray-400">
            {state.loading ? '…' : `${visible.length} / ${state.rows.length}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                  filter === f.value
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-red-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm theo nội dung, tên, email…"
            className="ml-auto w-64 max-w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {state.error && (
          <div className="bg-red-50 text-red-700 text-sm rounded-xl px-5 py-4 mb-6 border border-red-100">
            {state.error}
          </div>
        )}

        {state.loading ? (
          <p className="text-sm text-gray-400">Đang tải…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-400">Không có gợi ý nào khớp.</p>
        ) : (
          <ul className="brand-card divide-y divide-[rgba(109,200,181,0.25)] overflow-hidden">
            {visible.map((row) => (
              <li key={row.id}>
                <Link
                  to={`/admin/tips/${row.id}`}
                  className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 truncate">{truncate(row.tip)}</p>
                      <span
                        className={`shrink-0 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded border ${STATUS_PILL[row.status]}`}
                      >
                        {STATUS_LABEL[row.status]}
                      </span>
                      {row.replied_at && (
                        <span
                          title="Đã trả lời"
                          aria-label="Đã trả lời"
                          className="shrink-0 text-emerald-500 text-sm leading-none"
                        >
                          ✉
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {row.submitter_name || '—'}
                      {row.submitter_email ? ` · ${row.submitter_email}` : ''}
                      {row.country ? ` · ${row.country}` : ''}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 shrink-0 flex items-center gap-3">
                    <span>{new Date(row.created_at).toLocaleDateString('vi-VN')}</span>
                    <span>→</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
